import { createContext, useCallback, useMemo, useContext, useState, type FC, type ReactNode } from 'react';

import { readFederatedCurrentUser } from '@atlas-sp/aga-common-lib/current-user';

import type { CurrentUserContextType } from '@/providers/CurrentUserProvider/CurrentUserContext';
import {
  resolveNavigationCurrentUser,
  toHostNavigationCurrentUser,
} from '@/lib/moduleFederation/runtime/federatedNavigationUserWindow';

/**
 * Navigation and User context for aga-frontend
 * Mirrors the structure in aga-components for consistency
 */
export interface NavigationContextType {
  navigate: (path: string) => void;
  goBack: () => void;
  replace: (path: string) => void;
  prefetch: (path: string) => void;
  pathname: string;
  query: Record<string, any>;
  asPath: string;
  currentUser?: CurrentUserContextType;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

/** Context for descendants (e.g. LoggedInLayout) to set currentUser so it's available synchronously in the main context */
type SetNavigationCurrentUser = (user: NavigationContextType['currentUser']) => void;
const SetNavigationCurrentUserContext = createContext<SetNavigationCurrentUser | null>(null);

interface NavigationProviderProps {
  children: ReactNode;
  navigation?: Partial<NavigationContextType>;
  // Legacy props for backward compatibility - prefer using navigation prop
  onNavigate?: (path: string) => void;
  onGoBack?: () => void;
  onReplace?: (path: string) => void;
  onPrefetch?: (path: string) => void;
  pathname?: string;
  query?: Record<string, any>;
  asPath?: string;
}

/**
 * NavigationProvider for aga-frontend
 * Provides navigation functionality and current user data to components
 *
 * Navigation can be provided as a single object or as individual props (legacy).
 * Prefer using the `navigation` prop for cleaner API.
 */
export const NavigationProvider: FC<NavigationProviderProps> = ({
  children,
  navigation,
  // Legacy props
  onNavigate,
  onGoBack,
  onReplace,
  onPrefetch,
  pathname = '/',
  query = {},
  asPath: providedAsPath,
}) => {
  // Use navigation object if provided, otherwise fall back to individual props
  const navNavigate = navigation?.navigate ?? onNavigate;
  const navGoBack = navigation?.goBack ?? onGoBack;
  const navReplace = navigation?.replace ?? onReplace;
  const navPrefetch = navigation?.prefetch ?? onPrefetch;
  const navPathname = navigation?.pathname ?? pathname;
  const navQuery = navigation?.query ?? query;
  const [currentUser, setCurrentUser] = useState<NavigationContextType['currentUser']>(() =>
    typeof window === 'undefined' ? undefined : toHostNavigationCurrentUser(readFederatedCurrentUser()),
  );

  const navigate = useCallback(
    (path: string) => {
      if (navNavigate) {
        navNavigate(path);
      } else {
        // Fallback to window.location for standalone usage
        window.location.href = path;
      }
    },
    [navNavigate],
  );

  const goBack = useCallback(() => {
    if (navGoBack) {
      navGoBack();
    } else {
      // Fallback to browser back
      window.history.back();
    }
  }, [navGoBack]);

  const replace = useCallback(
    (path: string) => {
      if (navReplace) {
        navReplace(path);
      } else {
        // Fallback to window.location for standalone usage
        window.location.replace(path);
      }
    },
    [navReplace],
  );

  const prefetch = useCallback(
    (path: string) => {
      if (navPrefetch) {
        navPrefetch(path);
      }
      // No fallback for prefetch - it's optional
    },
    [navPrefetch],
  );

  // Compute asPath from pathname and query, or use provided value
  // Priority: navigation.asPath > providedAsPath > computed from pathname/query
  const asPath = useMemo(() => {
    if (navigation?.asPath) {
      return navigation.asPath;
    }
    if (providedAsPath) {
      return providedAsPath;
    }
    // Construct asPath from pathname and query
    const queryString = Object.keys(navQuery)
      .map(key => {
        const value = navQuery[key];
        if (Array.isArray(value)) {
          return value.map(v => `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`).join('&');
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
      })
      .filter(Boolean)
      .join('&');
    return queryString ? `${navPathname}?${queryString}` : navPathname;
  }, [navigation?.asPath, providedAsPath, navPathname, navQuery]);

  const contextValue: NavigationContextType = useMemo(
    () => ({
      navigate,
      goBack,
      replace,
      prefetch,
      pathname: navPathname,
      query: navQuery,
      asPath,
      currentUser,
    }),
    [navigate, goBack, replace, prefetch, navPathname, navQuery, asPath, currentUser],
  );

  const federatedNavigationContext = useMemo(() => {
    const resolvedCurrentUser = resolveNavigationCurrentUser(currentUser);
    if (!resolvedCurrentUser) {
      return contextValue;
    }

    return {
      ...contextValue,
      currentUser: resolvedCurrentUser,
    };
  }, [contextValue, currentUser]);

  // Expose context globally for federated components (aga-components WithPermission reads currentUser here).
  if (typeof window !== 'undefined') {
    (window as any).__federatedNavigationContext = federatedNavigationContext;
  }

  const setCurrentUserMemo = useMemo(() => setCurrentUser, []);

  return (
    <SetNavigationCurrentUserContext.Provider value={setCurrentUserMemo}>
      <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>
    </SetNavigationCurrentUserContext.Provider>
  );
};

/**
 * Hook to access navigation functions
 */
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    // Return fallback functions if no context is provided
    return {
      navigate: (path: string) => (window.location.href = path),
      goBack: () => window.history.back(),
      replace: (path: string) => window.location.replace(path),
      prefetch: () => {},
      pathname: window.location.pathname,
      query: {},
      asPath: window.location.pathname + window.location.search,
      currentUser: undefined,
    };
  }
  return context;
};

/**
 * Hook for layout (e.g. LoggedInLayout) to set currentUser on the navigation context.
 * Calling this ensures currentUser is available synchronously in useNavigation() and on
 * window.__federatedNavigationContext, avoiding races where it was previously set only in useEffect.
 */
export const useSetNavigationCurrentUser = () => useContext(SetNavigationCurrentUserContext);
